import { useCallback, useState } from 'react'
import { fetchAllTemplates } from '../templateService'

type TemplateList = {
    id: string
    committee_id: string
    title: string
    updated_at: string
    updated_by: string
    site?: string
    originalFileName?: string
    description?: string
    committee_name: string
    isDefault?: boolean
}

export const useMinutesTemplates = () => {
    const [templateLists, setTemplateLists] = useState<TemplateList[]>([])
    const [isLoading, setLoading] = useState(false)
    const [totalTemplates, setTotalTemplates] = useState(0)
    const [systemTemplate, setSystemTemplate] = useState<TemplateList[]>([])

    const fetchMinutesTemplates = useCallback(
        async (page?: any, sortBy?: any, order?: any, perPage?: any) => {
            setLoading(true)
            try {
                let data = await fetchAllTemplates(page, sortBy, order, perPage)
                // Handle empty response gracefully
                if (!data?.data || data?.data?.length === 0) {
                    // Don't update templateLists if we get empty response
                    // This prevents the list from becoming blank
                    setSystemTemplate(data?.systemTemplates)
                    setTotalTemplates(data?.pagination?.totalItems || 0)
                    setLoading(false)
                    return
                }

                const templates =
                    data?.data?.length > 0 &&
                    data?.data?.map((obj) => ({
                        ...obj
                    }))
                if (data?.data?.length > 0 && page > 1) {
                    setTemplateLists((prev) => [...prev, ...templates])
                } else if (data?.systemTemplates?.length > 0) {
                    setSystemTemplate(data?.systemTemplates)
                    setTemplateLists(templates)
                } else {
                    const templates = data?.data?.map((obj) => ({ ...obj }))
                    setSystemTemplate(data?.systemTemplates)
                    setTemplateLists(templates)
                }
                setTotalTemplates(data?.pagination?.totalItems)
            } catch (error) {
                setLoading(false)
                console.error(error)
            } finally {
                setLoading(false)
            }
        },
        []
    )

    const refetchAndReplaceLastPage = useCallback(
        async (lastPage: number, sortBy?: any, order?: any, perPage?: any) => {
            try {
                // Fetch the last page data
                const data = await fetchAllTemplates(lastPage, sortBy, order, perPage)

                // Handle empty response gracefully
                if (!data?.data || data?.data?.length === 0) {
                    // Don't update templateLists if we get empty response
                    // This prevents the list from becoming blank
                    setTotalTemplates(data?.pagination?.totalItems || 0)

                    // Refresh system templates even when last page is empty
                    if (Array.isArray(data?.systemTemplates)) {
                        setSystemTemplate(data.systemTemplates)
                    } else {
                        try {
                            const firstPageData = await fetchAllTemplates(1, sortBy, order, perPage)
                            if (Array.isArray(firstPageData?.systemTemplates)) {
                                setSystemTemplate(firstPageData.systemTemplates)
                            }
                        } catch (err) {
                            // Ignore errors while refreshing system templates
                        }
                    }

                    return
                }

                if (data?.data?.length > 0) {
                    // Build new page templates without slicing
                    const newPageTemplates = data?.data?.map((obj) => ({ ...obj }))

                    // Calculate how many templates to keep from previous pages
                    const itemsPerPage = perPage
                    const keepFromPreviousPages = (lastPage - 1) * itemsPerPage

                    setTemplateLists((prev) => {
                        // Keep only the items from previous pages and replace with new last page data
                        const previousPagesData = prev.slice(0, keepFromPreviousPages)
                        return [...previousPagesData, ...newPageTemplates]
                    })

                    // Update total count from server
                    setTotalTemplates(data?.pagination?.totalItems)
                }

                // Also refresh system templates from the first page, as default state may change
                try {
                    const firstPageData = await fetchAllTemplates(1, sortBy, order, perPage)
                    if (Array.isArray(firstPageData?.systemTemplates)) {
                        setSystemTemplate(firstPageData.systemTemplates)
                    }
                } catch (err) {
                    // Ignore errors while refreshing system templates
                }
            } catch (error) {
                console.log('Error refetching last page:', error)
            }
        },
        []
    )

    const refreshTemplateList = useCallback(
        async (page?: any, sortBy?: any, order?: any, perPage?: any) => {
            setLoading(true)
            try {
                let data = await fetchAllTemplates(page, sortBy, order, perPage)

                // Handle empty response gracefully
                if (!data?.data || data?.data?.length === 0) {
                    setSystemTemplate(data?.systemTemplates || [])
                    setTemplateLists([])
                    setTotalTemplates(data?.pagination?.totalItems || 0)
                    setLoading(false)
                    return
                }

                // Always replace the list for refresh operations
                setSystemTemplate(data?.systemTemplates)
                setTemplateLists(data?.data)
                setTotalTemplates(data?.pagination?.totalItems)
            } catch (error) {
                setLoading(false)
                console.error(error)
            } finally {
                setLoading(false)
            }
        },
        []
    )

    return {
        templateLists,
        fetchMinutesTemplates,
        refetchAndReplaceLastPage,
        refreshTemplateList,
        setTemplateLists,
        setTotalTemplates,
        isLoading,
        totalTemplates,
        systemTemplate
    }
}
